import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { ChannelsContext, UserDataContext } from 'lane-shared/contexts';
import { getDisplayName } from 'lane-shared/helpers';
import {
  FONT_AWESOME_REGULAR,
  FONT_AWESOME_SOLID,
  ICON_SET_FONTAWESOME,
} from 'constants-activate';
import { useFlag } from 'lane-shared/hooks';
import {
  PropertyTenantChannel,
  useLatestRef,
  usePropertyTenantsByRelationship,
} from 'react-hooks';
import { FeatureFlag } from 'constants-flags';
import { setDefaultChannelMutation } from 'graphql-queries';

import { convertTo62 } from 'uuid-encoding';
import { datadogRum } from '@datadog/browser-rum';
import { Icon } from 'design-system-web';

import Button from '../general/Button';
import { HomeChannelPickerModal } from './HomeChannelPickerModal';
import styles from './HomeChannelSettings.scss';

export const HomeChannelSettings = memo(function HomeChannelSettings() {
  const { t } = useTranslation();
  const isHomeChannelSelectionEnabled = useFlag(
    FeatureFlag.UserHomeChannelSelection,
    false
  );
  const { refetch: refetchUser, defaultChannelId } = useContext(UserDataContext);
  const { channels, refetch: refetchChannels } = useContext(ChannelsContext);
  const { tenantsByPropertyId, refetch: refetchTenants } = usePropertyTenantsByRelationship(
    channels,
    isHomeChannelSelectionEnabled && channels.length >= 2
  );
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [setDefaultChannel] = useMutation(setDefaultChannelMutation);

  const homeChannelId = useMemo(
    () => (defaultChannelId ? convertTo62(defaultChannelId) : null),
    [defaultChannelId]
  );
  const allTenantChannels = useMemo(
    () => Array.from(tenantsByPropertyId.values()).flat(),
    [tenantsByPropertyId]
  );
  const homeChannel = useMemo(
    () =>
      channels.find(channel => channel._id === homeChannelId) ??
      allTenantChannels.find(channel => channel._id === homeChannelId),
    [channels, allTenantChannels, homeChannelId]
  );

  const refetchersRef = useLatestRef({
    refetchChannels,
    refetchTenants,
    refetchUser,
  });

  const inFlightRefetchRef = useRef<Promise<unknown> | null>(null);

  const refetchHomeChannelData = useCallback(() => {
    if (inFlightRefetchRef.current) {
      return inFlightRefetchRef.current;
    }

    const refetchPromise = Promise.allSettled([
      refetchersRef.current.refetchChannels().catch((error: Error) => {
        datadogRum.addError(error, {
          component: 'HomeChannelSettings',
          action: 'refetchChannels',
        });
      }),
      refetchersRef.current.refetchTenants().catch((error: Error) => {
        datadogRum.addError(error, {
          component: 'HomeChannelSettings',
          action: 'refetchTenants',
        });
      }),
      refetchersRef.current.refetchUser().catch((error: Error) => {
        datadogRum.addError(error, {
          component: 'HomeChannelSettings',
          action: 'refetchUser',
        });
      }),
    ]).finally(() => {
      inFlightRefetchRef.current = null;
    });

    inFlightRefetchRef.current = refetchPromise;
    return refetchPromise;
  }, [refetchersRef]);

  useEffect(() => {
    if (isHomeChannelSelectionEnabled) {
      refetchHomeChannelData();
    }
  }, [isHomeChannelSelectionEnabled, refetchHomeChannelData]);

  const openPicker = useCallback(() => {
    setIsPickerOpen(true);
    refetchHomeChannelData();
  }, [refetchHomeChannelData]);

  const closePicker = useCallback(() => setIsPickerOpen(false), []);

  const setHomeChannel = useCallback(
    async (channel: PropertyTenantChannel) => {
      try {
        await setDefaultChannel({ variables: { channelId: channel._id } });

        inFlightRefetchRef.current = null;
        await refetchersRef.current.refetchUser().catch((error: Error) => {
          datadogRum.addError(error, {
            component: 'setHomeChannel',
            action: 'refetchUser',
          });
        });

        const successMessage = t('web.userProfile.homeChannel.successToast', {
          channelName: getDisplayName(channel),
        });

        if (window.Toast) {
          window.Toast.show(successMessage);
        }
      } catch (error) {
        datadogRum.addError(error, {
          component: 'HomeChannelSettings',
          action: 'setDefaultChannel',
        });

        const errorMessage = t('web.userProfile.homeChannel.errorToast');

        if (window.Toast) {
          window.Toast.show(errorMessage);
        }
      }
    },
    [setDefaultChannel, refetchersRef, t]
  );

  if (!isHomeChannelSelectionEnabled || channels.length < 2) {
    return null;
  }

  const buttonLabel = homeChannel
    ? t('web.userProfile.homeChannel.change')
    : t('web.userProfile.homeChannel.setHomeChannel');

  return (
    <>
      <div
        className={styles.HomeChannelSettings}
        data-test="homeChannelSettings"
      >
        <div className={styles.label}>
          <div
            className={`${styles.iconContainer} ${
              homeChannel ? styles.hasSelectedChannel : ''
            }`}
          >
            <Icon
              className={styles.icon}
              name="home"
              set={ICON_SET_FONTAWESOME}
              type={homeChannel ? FONT_AWESOME_SOLID : FONT_AWESOME_REGULAR}
            />
          </div>
          {homeChannel ? (
            <div className={styles.content}>
              <div className={styles.title}>
                {t('web.userProfile.homeChannel.label')}
              </div>
              <div className={styles.channelName}>
                {getDisplayName(homeChannel)}
              </div>
            </div>
          ) : (
            <div className={styles.placeholder}>
              {t('web.userProfile.homeChannel.noHomeChannelSet')}
            </div>
          )}
        </div>
        <Button
          className={`${styles.button} ${homeChannel ? styles.filled : ''}`}
          variant="text"
          onClick={openPicker}
          testId="homeChannelCTA"
        >
          {buttonLabel}
        </Button>
      </div>
      <HomeChannelPickerModal
        isOpen={isPickerOpen}
        onClose={closePicker}
        channels={channels}
        currentHomeChannelId={homeChannelId}
        onConfirm={setHomeChannel}
        tenantsByPropertyId={tenantsByPropertyId}
      />
    </>
  );
});